import * as React from 'react';
import { cn } from '@/utils/cn';
import { X } from 'lucide-react';

interface DrawerProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}

interface DrawerContentProps {
  children?: React.ReactNode;
  className?: string;
}

interface DrawerHeaderProps {
  children?: React.ReactNode;
  className?: string;
}

interface DrawerTitleProps {
  children?: React.ReactNode;
  className?: string;
}

interface DrawerDescriptionProps {
  children?: React.ReactNode;
  className?: string;
}

interface DrawerFooterProps {
  children?: React.ReactNode;
  className?: string;
}

function Drawer({ open = false, onOpenChange, children }: DrawerProps) {
  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50 animate-in fade-in duration-200"
        onClick={() => onOpenChange?.(false)}
      />
      <div className="fixed inset-y-0 right-0 z-50 flex animate-in slide-in-from-right duration-300">
        {children}
      </div>
    </>
  );
}

function DrawerContent({ children, className }: DrawerContentProps) {
  return (
    <div
      className={cn(
        'bg-white h-full w-full max-w-md shadow-xl flex flex-col',
        className
      )}
    >
      {children}
    </div>
  );
}

function DrawerHeader({ children, className }: DrawerHeaderProps) {
  return (
    <div className={cn('px-6 py-4 border-b border-gray-200 flex items-center justify-between', className)}>
      {children}
    </div>
  );
}

function DrawerTitle({ children, className }: DrawerTitleProps) {
  return (
    <h2 className={cn('text-lg font-semibold text-gray-900', className)}>
      {children}
    </h2>
  );
}

function DrawerDescription({ children, className }: DrawerDescriptionProps) {
  return (
    <p className={cn('text-sm text-gray-500 mt-1', className)}>
      {children}
    </p>
  );
}

function DrawerFooter({ children, className }: DrawerFooterProps) {
  return (
    <div className={cn('mt-auto px-6 py-4 border-t border-gray-200 flex justify-end gap-3', className)}>
      {children}
    </div>
  );
}

function DrawerClose({ onClose, children }: { onClose?: () => void; children?: React.ReactNode }) {
  return (
    <button
      onClick={onClose}
      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
    >
      <X className="h-5 w-5" />
      {children}
    </button>
  );
}

export {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
};
